import { Button } from '@/components/Button';
import Input from '@/components/Input';
import Select from '@/components/Select';
import MultiSelect from '@/components/MultiSelect';
import MultiSelectManageLink from '@/components/MultiSelectManageLink';
import CustomTagMatchMenu, { type CustomTagMatch } from '@/components/CustomTagMatchMenu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useEffect, useState, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { MdAdd, MdArrowDropDown, MdOutlineClose, MdSettings } from 'react-icons/md';
import { COMPANY_SEARCH_ITEMS, LISTED_PHASE_ITEMS } from '../constants';
import useFetchIndustries from '../../api/useFetchIndustries';
import type SharedCustomField from '@/types/serializers/SharedCustomField';
import 'react-datepicker/dist/react-datepicker.css';
import CandidatesDateField from '../../candidates/components/CandidatesDateField';
import useFetchIam from '../../api/useFetchIam';
import useFetchActiveUsers from '../../api/useFetchActiveUsers';
import useCompanyTagsList from '../hooks/useCompanyTagsList';
import { Link } from '@tanstack/react-router';

type CustomFieldValue =
  | string
  | number
  | number[]
  | { min?: number | null | string; max?: number | null | string }
  | null;

type CustomFieldSearch = {
  fieldId: number;
  fieldType: string;
  fieldName: string;
  value: CustomFieldValue;
  options?: Array<{ id: number; value: string }>;
};

type FormValues = {
  query: string;
  industryId: string;
  listedPhase: string;
  address: string;
  numberOfEmployeesMin?: number;
  numberOfEmployeesMax?: number;
  establishedDateMin?: number;
  establishedDateMax?: number;
  description: string;
  organizationUserId: string;
  customTagIds: number[];
  customTagMatch: CustomTagMatch;
  selectedSearchItems: SearchItem[];
  customFields: Record<number, CustomFieldSearch>;
};

type Props = {
  total: number;
  onSearch: (
    query: string,
    industryId: string,
    listedPhase: string,
    address: string,
    numberOfEmployees: { min?: number; max?: number },
    establishedDate: { min?: number; max?: number },
    description?: string,
    organizationUserId?: string,
    customFields?: { field_id: number; content: unknown }[],
    customTagIds?: number[],
    customTagMatch?: CustomTagMatch
  ) => void;
  query: string;
  industryId: string;
  listedPhase: string;
  address: string;
  numberOfEmployees?: { min?: number; max?: number };
  establishedDate?: { min?: number; max?: number };
  description?: string;
  organizationUserId?: string;
  customTagIds?: number[];
  customTagMatch?: CustomTagMatch;
  customFields?: SharedCustomField[];
  handleOpenModal: () => void;
};

type SearchItem = {
  label: string;
  type: string;
};

type RemoveButtonProps = {
  onClick: () => void;
};

function RemoveButton({ onClick }: RemoveButtonProps) {
  return (
    <button type="button" className="flex size-6 items-center justify-center" onClick={onClick}>
      <MdOutlineClose className="size-6 text-[#6D6D6D]" />
    </button>
  );
}

export default function CompaniesSearchBox({
  total,
  onSearch,
  query: initialQuery = '',
  industryId: initialIndustryId = '',
  listedPhase: initialListedPhase = '',
  address: initialAddress = '',
  numberOfEmployees: initialNumberOfEmployees = {},
  establishedDate: initialEstablishedDate = {},
  description: initialDescription = '',
  organizationUserId: initialOrganizationUserId = '',
  customTagIds: initialCustomTagIds = [],
  customTagMatch: initialCustomTagMatch = 'all',
  customFields = [],
  handleOpenModal,
}: Props) {
  const { data: industries } = useFetchIndustries();
  const { data: iamData } = useFetchIam();
  const { data: activeUsersData } = useFetchActiveUsers();
  const { data: companyTagsData } = useCompanyTagsList();
  const [isSearchPopoverOpen, setIsSearchPopoverOpen] = useState(false);
  const [selectFieldValues, setSelectFieldValues] = useState<Record<number, number>>({});

  const { control, setValue, watch, reset } = useForm<FormValues>({
    defaultValues: {
      query: initialQuery,
      industryId: initialIndustryId,
      listedPhase: initialListedPhase,
      address: initialAddress,
      numberOfEmployeesMin: initialNumberOfEmployees?.min,
      numberOfEmployeesMax: initialNumberOfEmployees?.max,
      establishedDateMin: initialEstablishedDate?.min,
      establishedDateMax: initialEstablishedDate?.max,
      description: initialDescription,
      organizationUserId: initialOrganizationUserId,
      customTagIds: initialCustomTagIds,
      customTagMatch: initialCustomTagMatch,
      selectedSearchItems: [],
      customFields: {},
    },
  });

  const formValues = useWatch({ control });
  const selectedSearchItems = watch('selectedSearchItems');
  const customFieldValues = watch('customFields');

  const isFilterActive =
    formValues.query !== '' ||
    formValues.industryId !== '' ||
    formValues.listedPhase !== '' ||
    formValues.address !== '' ||
    formValues.description !== '' ||
    formValues.organizationUserId !== '' ||
    formValues.numberOfEmployeesMin !== undefined ||
    formValues.numberOfEmployeesMax !== undefined ||
    formValues.establishedDateMin !== undefined ||
    formValues.establishedDateMax !== undefined ||
    (formValues.customTagIds || []).length > 0 ||
    Object.keys(formValues.customFields || {}).length > 0;

  const clearSearchFilters = () => {
    reset({
      query: '',
      industryId: '',
      listedPhase: '',
      address: '',
      description: '',
      organizationUserId: '',
      customTagIds: [],
      customTagMatch: 'all',
      numberOfEmployeesMin: undefined,
      numberOfEmployeesMax: undefined,
      establishedDateMin: undefined,
      establishedDateMax: undefined,
      selectedSearchItems: [],
      customFields: {},
    });
    setSelectFieldValues({});
  };

  useEffect(() => {
    setValue('query', initialQuery);
    setValue('industryId', initialIndustryId);
    setValue('listedPhase', initialListedPhase);
    setValue('address', initialAddress);
    setValue('description', initialDescription);
    setValue('organizationUserId', initialOrganizationUserId);
    setValue('customTagIds', initialCustomTagIds);
    setValue('customTagMatch', initialCustomTagMatch);
    setValue('numberOfEmployeesMin', initialNumberOfEmployees?.min);
    setValue('numberOfEmployeesMax', initialNumberOfEmployees?.max);
    setValue('establishedDateMin', initialEstablishedDate?.min);
    setValue('establishedDateMax', initialEstablishedDate?.max);
  }, [
    initialQuery,
    initialIndustryId,
    initialListedPhase,
    initialAddress,
    initialDescription,
    initialOrganizationUserId,
    initialCustomTagIds,
    initialCustomTagMatch,
    initialNumberOfEmployees,
    initialEstablishedDate,
    setValue,
  ]);

  const executeSearch = () => {
    const numberOfEmployees = {
      ...(formValues.numberOfEmployeesMin !== undefined && {
        min: formValues.numberOfEmployeesMin,
      }),
      ...(formValues.numberOfEmployeesMax !== undefined && {
        max: formValues.numberOfEmployeesMax,
      }),
    };

    const establishedDate = {
      ...(formValues.establishedDateMin !== undefined && { min: formValues.establishedDateMin }),
      ...(formValues.establishedDateMax !== undefined && { max: formValues.establishedDateMax }),
    };

    // カスタムフィールドの検索条件を作成
    const customFieldsForSearch = Object.entries(formValues.customFields || {})
      .filter(([_, field]) => {
        // 明示的に型を指定
        const customField: CustomFieldSearch | undefined = field as CustomFieldSearch | undefined;
        return hasCustomFieldValue(customField);
      })
      .map(([fieldId, field]) => ({
        field_id: Number(fieldId),
        content: formatCustomFieldValueForSearch(field as CustomFieldSearch | undefined),
      }));

    onSearch(
      formValues.query || '',
      formValues.industryId || '',
      formValues.listedPhase || '',
      formValues.address || '',
      numberOfEmployees,
      establishedDate,
      formValues.description || '',
      formValues.organizationUserId || '',
      customFieldsForSearch.length > 0 ? customFieldsForSearch : undefined,
      formValues.customTagIds && formValues.customTagIds.length > 0
        ? formValues.customTagIds
        : undefined,
      formValues.customTagMatch ?? 'all'
    );
  };

  // カスタムフィールドの値が存在するかチェック
  const hasCustomFieldValue = (field: CustomFieldSearch | undefined): boolean => {
    if (!field) return false;
    const { value, fieldType } = field;

    switch (fieldType) {
      case 'short_text':
      case 'long_text':
        return typeof value === 'string' && value.trim() !== '';
      case 'single_select':
        return value !== null && value !== undefined;
      case 'multiple_select':
        return Array.isArray(value) && value.length > 0;
      case 'number':
      case 'date':
      case 'datetime':
        if (typeof value === 'object' && value !== null && 'min' in value && 'max' in value) {
          return value.min !== null || value.max !== null;
        }
        return false;
      default:
        return false;
    }
  };

  const formatCustomFieldValueForSearch = (field: CustomFieldSearch | undefined): unknown => {
    if (!field) return '';
    const { value, fieldType } = field;

    if (fieldType === 'number' || fieldType === 'date' || fieldType === 'datetime') {
      if (typeof value === 'object' && value !== null && 'min' in value && 'max' in value) {
        const result: { min?: number | string; max?: number | string } = {};
        if (value.min !== null) result.min = value.min;
        if (value.max !== null) result.max = value.max;
        return result;
      }
    }

    if (value === null) {
      return '';
    }

    return value;
  };

  const addCustomField = useCallback(
    (field: SharedCustomField) => {
      if (customFieldValues) {
        setValue('customFields', {
          ...customFieldValues,
          [field.id]: {
            fieldId: field.id,
            fieldType: field.field_type,
            fieldName: field.name,
            value: getInitialCustomFieldValue(field.field_type),
            options: field.custom_field_options,
          },
        });
      }
    },
    [setValue, customFieldValues]
  );

  const getInitialCustomFieldValue = (fieldType: string): CustomFieldValue => {
    switch (fieldType) {
      case 'number':
        return { min: null, max: null };
      case 'date':
      case 'datetime':
        return { min: null, max: null };
      case 'short_text':
      case 'long_text':
        return '';
      case 'single_select':
        return null;
      case 'multiple_select':
        return [];
      default:
        return null;
    }
  };

  const removeCustomField = useCallback(
    (fieldId: number) => {
      if (customFieldValues) {
        const newCustomFields = { ...customFieldValues };
        delete newCustomFields[fieldId];
        setValue('customFields', newCustomFields);

        const newSelectFieldValues = { ...selectFieldValues };
        delete newSelectFieldValues[fieldId];
        setSelectFieldValues(newSelectFieldValues);
      }
    },
    [customFieldValues, setValue, selectFieldValues]
  );

  const updateCustomFieldValue = useCallback(
    (fieldId: number, value: CustomFieldValue) => {
      if (customFieldValues && customFieldValues[fieldId]) {
        const updatedFields = {
          ...customFieldValues,
          [fieldId]: {
            ...customFieldValues[fieldId],
            value,
          },
        };
        setValue('customFields', updatedFields);
      }
    },
    [setValue, customFieldValues]
  );

  const handleSelectChange = useCallback(
    (fieldId: number, value: number) => {
      setSelectFieldValues((prev) => ({
        ...prev,
        [fieldId]: value,
      }));
      updateCustomFieldValue(fieldId, value);
    },
    [updateCustomFieldValue]
  );

  // NOTE: フォームの値が変更されたら検索を実行（ソート以外の値の変更のみ）
  useEffect(() => {
    const timer = setTimeout(() => {
      executeSearch();
    }, 200);

    return () => clearTimeout(timer);
  }, [
    formValues.query,
    formValues.industryId,
    formValues.listedPhase,
    formValues.address,
    formValues.description,
    formValues.organizationUserId,
    formValues.customTagIds,
    formValues.customTagMatch,
    formValues.numberOfEmployeesMin,
    formValues.numberOfEmployeesMax,
    formValues.establishedDateMin,
    formValues.establishedDateMax,
    formValues.customFields,
  ]);

  // NOTE: 検索項目の初期化と更新
  useEffect(() => {
    const initialItems: SearchItem[] = [];

    // 従業員数
    if (
      formValues.numberOfEmployeesMin !== undefined ||
      formValues.numberOfEmployeesMax !== undefined
    ) {
      const employeeItem = COMPANY_SEARCH_ITEMS.find((item) => item.label === '従業員数');
      if (employeeItem && !selectedSearchItems.some((item) => item.label === '従業員数')) {
        initialItems.push(employeeItem);
      }
    }

    // 設立年
    if (
      formValues.establishedDateMin !== undefined ||
      formValues.establishedDateMax !== undefined
    ) {
      const establishedItem = COMPANY_SEARCH_ITEMS.find((item) => item.label === '設立年');
      if (establishedItem && !selectedSearchItems.some((item) => item.label === '設立年')) {
        initialItems.push(establishedItem);
      }
    }

    // 所在地
    if (formValues.address !== '') {
      const addressItem = COMPANY_SEARCH_ITEMS.find((item) => item.label === '所在地');
      if (addressItem && !selectedSearchItems.some((item) => item.label === '所在地')) {
        initialItems.push(addressItem);
      }
    }

    // 事業内容
    if (formValues.description !== '') {
      const descriptionItem = COMPANY_SEARCH_ITEMS.find((item) => item.label === '事業内容');
      if (descriptionItem && !selectedSearchItems.some((item) => item.label === '事業内容')) {
        initialItems.push(descriptionItem);
      }
    }

    if (initialItems.length > 0) {
      const newSelectedItems = [...selectedSearchItems];
      initialItems.forEach((item) => {
        if (!newSelectedItems.some((existingItem) => existingItem.label === item.label)) {
          newSelectedItems.push(item);
        }
      });
      setValue('selectedSearchItems', newSelectedItems);
    }
  }, [
    formValues.address,
    formValues.numberOfEmployeesMin,
    formValues.numberOfEmployeesMax,
    formValues.establishedDateMin,
    formValues.establishedDateMax,
    formValues.description,
    selectedSearchItems,
    setValue,
  ]);

  return (
    <div className="">
      <div className="flex">
        <p className="flex items-end gap-1 text-base">
          <span className="text-2xl font-bold">{total}</span>社
        </p>
        <Button color="primary" size="md" onClick={handleOpenModal} className="ml-auto">
          <MdAdd className="size-icon-sm" />
          企業を登録
        </Button>
        {iamData?.current_user?.is_admin && (
          <Button color="secondary" size="md" asChild iconOnly className="ml-xs">
            <Link to="/agent/customize" search={{ type: 'company' as const }}>
              <MdSettings className="size-icon-xs" />
            </Link>
          </Button>
        )}
      </div>
      <div className="mt-md rounded bg-white p-md shadow-xs">
        <div>
          <div className="flex items-center gap-xl">
            <div className="flex w-[300px] flex-col gap-2">
              <label className="m-0 text-sm font-bold">フリーワード</label>
              <Input
                search
                className="w-full"
                autoComplete="off"
                placeholder="フリーワードで絞り込む"
                value={formValues.query}
                onChange={(e) => setValue('query', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            <div className="flex w-[250px] flex-col gap-2">
              <label className="m-0 text-sm font-bold">上場情報</label>
              <Select
                className="w-full"
                items={LISTED_PHASE_ITEMS}
                defaultLabel=""
                value={formValues.listedPhase}
                onValueChange={(value) => setValue('listedPhase', value)}
              />
            </div>
            <div className="flex w-[250px] flex-col gap-2">
              <label className="m-0 text-sm font-bold">業種</label>
              <Select
                className="w-full"
                items={
                  industries?.map((i) => {
                    return {
                      value: i.id,
                      label: i.name,
                    };
                  }) ?? []
                }
                defaultLabel=""
                value={formValues.industryId}
                onValueChange={(value) => setValue('industryId', value)}
              />
            </div>
            <div className="flex w-[250px] flex-col gap-2">
              <label className="m-0 text-sm font-bold">担当者</label>
              <Select
                className="w-full"
                items={[
                  { value: 'all', label: 'すべて表示' },
                  { value: 'unassigned', label: '未設定' },
                  ...(activeUsersData?.records?.map((user) => {
                    return {
                      value: String(user.id),
                      label: user.name,
                    };
                  }) ?? []),
                ]}
                defaultLabel=""
                value={
                  formValues.organizationUserId === '' || formValues.organizationUserId === 'all'
                    ? 'all'
                    : formValues.organizationUserId === '-1' ||
                        formValues.organizationUserId === 'unassigned'
                      ? 'unassigned'
                      : formValues.organizationUserId
                }
                onValueChange={(value) => {
                  if (value === 'all') {
                    setValue('organizationUserId', '');
                  } else if (value === 'unassigned') {
                    setValue('organizationUserId', '-1');
                  } else {
                    setValue('organizationUserId', value);
                  }
                }}
              />
            </div>
          </div>
          {companyTagsData?.tags && companyTagsData.tags.length > 0 && (
            <div className="mt-4 flex w-full flex-col gap-xs">
              <label className="m-0 text-heading-xxs">タグ</label>
              <MultiSelect
                options={companyTagsData.tags}
                value={formValues.customTagIds || []}
                onChange={(value) => setValue('customTagIds', value)}
                placeholder="タグで絞り込む"
                searchable
                variant="list"
                header={
                  <CustomTagMatchMenu
                    value={formValues.customTagMatch}
                    onChange={(value) => setValue('customTagMatch', value)}
                  />
                }
                footer={
                  <MultiSelectManageLink to="/agent/company_tags">タグを管理</MultiSelectManageLink>
                }
              />
            </div>
          )}
          <div className="mt-4 flex flex-col gap-md pt-3">
            {selectedSearchItems.length > 0 && (
              <div className="mb-4 flex flex-col gap-4">
                {Array.from({ length: Math.ceil(selectedSearchItems.length / 3) }).map(
                  (_, rowIndex) => (
                    <div key={rowIndex} className="flex gap-xl">
                      {selectedSearchItems
                        .slice(rowIndex * 3, rowIndex * 3 + 3)
                        .map((item, index) => (
                          <div key={index} className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              <label className="m-0 text-sm font-bold">{item.label}</label>
                            </div>
                            {item.type === 'text' ? (
                              item.label === '所在地' ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    className="w-[300px]"
                                    autoComplete="off"
                                    value={formValues.address}
                                    onChange={(e) => {
                                      setValue('address', e.target.value);
                                    }}
                                  />
                                  <RemoveButton
                                    onClick={() => {
                                      setValue('address', '');
                                      setValue(
                                        'selectedSearchItems',
                                        selectedSearchItems.filter(
                                          (item) => item.label !== '所在地'
                                        )
                                      );
                                    }}
                                  />
                                </div>
                              ) : item.label === '事業内容' ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    className="w-[300px]"
                                    autoComplete="off"
                                    value={formValues.description}
                                    onChange={(e) => {
                                      setValue('description', e.target.value);
                                    }}
                                  />
                                  <RemoveButton
                                    onClick={() => {
                                      setValue('description', '');
                                      setValue(
                                        'selectedSearchItems',
                                        selectedSearchItems.filter(
                                          (item) => item.label !== '事業内容'
                                        )
                                      );
                                    }}
                                  />
                                </div>
                              ) : (
                                <Input className="w-[300px]" autoComplete="off" />
                              )
                            ) : item.type === 'number' ? (
                              <div className="flex items-center gap-2">
                                {item.label === '従業員数' ? (
                                  <div className="flex items-center gap-2">
                                    <Input
                                      className="w-[100px]"
                                      type="number"
                                      autoComplete="off"
                                      value={formValues.numberOfEmployeesMin || ''}
                                      onChange={(e) => {
                                        setValue(
                                          'numberOfEmployeesMin',
                                          e.target.value ? Number(e.target.value) : undefined
                                        );
                                      }}
                                    />
                                    <span>〜</span>
                                    <Input
                                      className="w-[100px]"
                                      type="number"
                                      autoComplete="off"
                                      value={formValues.numberOfEmployeesMax || ''}
                                      onChange={(e) => {
                                        setValue(
                                          'numberOfEmployeesMax',
                                          e.target.value ? Number(e.target.value) : undefined
                                        );
                                      }}
                                    />
                                    <span className="text-sm">人</span>
                                    <RemoveButton
                                      onClick={() => {
                                        setValue('numberOfEmployeesMin', undefined);
                                        setValue('numberOfEmployeesMax', undefined);
                                        setValue(
                                          'selectedSearchItems',
                                          selectedSearchItems.filter(
                                            (item) => item.label !== '従業員数'
                                          )
                                        );
                                      }}
                                    />
                                  </div>
                                ) : item.label === '設立年' ? (
                                  <div className="flex items-center gap-2">
                                    <Input
                                      className="w-[100px]"
                                      type="number"
                                      autoComplete="off"
                                      value={formValues.establishedDateMin || ''}
                                      onChange={(e) => {
                                        setValue(
                                          'establishedDateMin',
                                          e.target.value ? Number(e.target.value) : undefined
                                        );
                                      }}
                                    />
                                    <span>〜</span>
                                    <Input
                                      className="w-[100px]"
                                      type="number"
                                      autoComplete="off"
                                      value={formValues.establishedDateMax || ''}
                                      onChange={(e) => {
                                        setValue(
                                          'establishedDateMax',
                                          e.target.value ? Number(e.target.value) : undefined
                                        );
                                      }}
                                    />
                                    <span className="text-sm">年</span>
                                    <RemoveButton
                                      onClick={() => {
                                        setValue('establishedDateMin', undefined);
                                        setValue('establishedDateMax', undefined);
                                        setValue(
                                          'selectedSearchItems',
                                          selectedSearchItems.filter(
                                            (item) => item.label !== '設立年'
                                          )
                                        );
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <>
                                    <Input className="w-[100px]" type="number" autoComplete="off" />
                                    <span>〜</span>
                                    <Input className="w-[100px]" type="number" autoComplete="off" />
                                  </>
                                )}
                              </div>
                            ) : null}
                          </div>
                        ))}
                    </div>
                  )
                )}
              </div>
            )}

            {/* カスタムフィールドの表示 */}
            {Object.values(customFieldValues || {}).length > 0 && (
              <div className="mb-4 flex flex-col gap-4">
                {Array.from({
                  length: Math.ceil(Object.values(customFieldValues || {}).length / 3),
                }).map((_, rowIndex) => (
                  <div key={`custom-${rowIndex}`} className="flex gap-xl">
                    {Object.values(customFieldValues || {})
                      .slice(rowIndex * 3, rowIndex * 3 + 3)
                      .map((field) => (
                        <div key={field.fieldId} className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <label className="m-0 text-sm font-bold">{field.fieldName}</label>
                          </div>
                          {field.fieldType === 'short_text' || field.fieldType === 'long_text' ? (
                            <div className="flex items-center gap-1">
                              <Input
                                className="w-[300px]"
                                autoComplete="off"
                                value={(field.value as string) || ''}
                                onChange={(e) =>
                                  updateCustomFieldValue(field.fieldId, e.target.value)
                                }
                              />
                              <RemoveButton onClick={() => removeCustomField(field.fieldId)} />
                            </div>
                          ) : field.fieldType === 'single_select' ? (
                            <div className="flex items-center gap-1">
                              <div className="w-[187px]">
                                <Select
                                  className="w-full"
                                  items={(field.options || []).map((option) => ({
                                    value: String(option.id),
                                    label: option.value,
                                  }))}
                                  defaultLabel={`${field.fieldName}を選択`}
                                  value={String(selectFieldValues[field.fieldId] || '')}
                                  onValueChange={(value) => {
                                    const numericValue = value ? Number(value) : 0;
                                    handleSelectChange(field.fieldId, numericValue);
                                  }}
                                />
                              </div>
                              <RemoveButton onClick={() => removeCustomField(field.fieldId)} />
                            </div>
                          ) : field.fieldType === 'multiple_select' ? (
                            <div className="flex items-center gap-1">
                              <div className="w-[187px]">
                                <MultiSelect
                                  options={(field.options || []).map((option) => ({
                                    id: option.id,
                                    name: option.value,
                                  }))}
                                  value={(field.value as number[]) || []}
                                  onChange={(value) => updateCustomFieldValue(field.fieldId, value)}
                                  placeholder={`${field.fieldName}を選択`}
                                />
                              </div>
                              <RemoveButton onClick={() => removeCustomField(field.fieldId)} />
                            </div>
                          ) : field.fieldType === 'number' ? (
                            <div className="flex items-center gap-1">
                              <div className="flex items-center gap-2">
                                <div className="w-[131px]">
                                  <Input
                                    className="w-full"
                                    autoComplete="off"
                                    placeholder=""
                                    type="number"
                                    value={
                                      typeof field.value === 'object' &&
                                      field.value &&
                                      'min' in field.value
                                        ? (field.value.min ?? '')
                                        : ''
                                    }
                                    onChange={(e) => {
                                      const currentValue =
                                        typeof field.value === 'object' &&
                                        field.value &&
                                        'min' in field.value &&
                                        'max' in field.value
                                          ? field.value
                                          : {
                                              min: null,
                                              max: null,
                                            };
                                      const newValue = e.target.value
                                        ? Number(e.target.value)
                                        : null;
                                      updateCustomFieldValue(field.fieldId, {
                                        ...currentValue,
                                        min: newValue,
                                      });
                                    }}
                                  />
                                </div>
                                <span className="text-sm font-bold">〜</span>
                                <div className="w-[131px]">
                                  <Input
                                    className="w-full"
                                    autoComplete="off"
                                    placeholder=""
                                    type="number"
                                    value={
                                      typeof field.value === 'object' &&
                                      field.value &&
                                      'max' in field.value
                                        ? (field.value.max ?? '')
                                        : ''
                                    }
                                    onChange={(e) => {
                                      const currentValue =
                                        typeof field.value === 'object' &&
                                        field.value &&
                                        'min' in field.value &&
                                        'max' in field.value
                                          ? field.value
                                          : {
                                              min: null,
                                              max: null,
                                            };
                                      const newValue = e.target.value
                                        ? Number(e.target.value)
                                        : null;
                                      updateCustomFieldValue(field.fieldId, {
                                        ...currentValue,
                                        max: newValue,
                                      });
                                    }}
                                  />
                                </div>
                              </div>
                              <RemoveButton onClick={() => removeCustomField(field.fieldId)} />
                            </div>
                          ) : field.fieldType === 'date' || field.fieldType === 'datetime' ? (
                            <div className="flex items-center gap-1">
                              <div className="w-[300px]">
                                <CandidatesDateField
                                  fieldId={field.fieldId}
                                  value={
                                    typeof field.value === 'object' &&
                                    field.value &&
                                    'min' in field.value &&
                                    'max' in field.value
                                      ? {
                                          min:
                                            typeof field.value.min === 'string'
                                              ? field.value.min
                                              : field.value.min?.toString() || null,
                                          max:
                                            typeof field.value.max === 'string'
                                              ? field.value.max
                                              : field.value.max?.toString() || null,
                                        }
                                      : { min: null, max: null }
                                  }
                                  onDateChange={(fieldId, min, max) => {
                                    updateCustomFieldValue(fieldId, { min, max });
                                  }}
                                />
                              </div>
                              <RemoveButton onClick={() => removeCustomField(field.fieldId)} />
                            </div>
                          ) : null}
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Popover
                open={isSearchPopoverOpen}
                onOpenChange={setIsSearchPopoverOpen}
                modal={true}
              >
                <PopoverTrigger asChild>
                  <Button
                    color="secondary"
                    size="md"
                    active={isSearchPopoverOpen}
                    className="w-[146px]"
                  >
                    検索条件を追加
                    <div className="min-h-md min-w-md">
                      <MdArrowDropDown
                        className={`size-icon-xs transition-transform duration-200 ${isSearchPopoverOpen ? 'rotate-180' : ''}`}
                      />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="rounded-sm bg-white p-0">
                  <div className="hidden-scrollbar flex max-h-[320px] w-[180px] flex-col overflow-hidden overflow-y-auto">
                    {/* 標準の検索項目 */}
                    {COMPANY_SEARCH_ITEMS.map((c, key) => {
                      const isAlreadySelected = selectedSearchItems.some(
                        (item) => item.label === c.label
                      );
                      if (isAlreadySelected) return null;

                      return (
                        <button
                          key={key}
                          type="button"
                          className="min-h-xxl px-3 text-left text-sm hover:bg-[#F6F6F6]"
                          onClick={() => {
                            setValue('selectedSearchItems', [...selectedSearchItems, c]);
                            setIsSearchPopoverOpen(false);
                          }}
                        >
                          {c.label}
                        </button>
                      );
                    })}

                    {customFields.length > 0 && (
                      <>
                        {COMPANY_SEARCH_ITEMS.some(
                          (c) => !selectedSearchItems.some((item) => item.label === c.label)
                        )}

                        {customFields.map((field) => {
                          const isAlreadySelected = Object.values(customFieldValues || {}).some(
                            (f) => f.fieldId === field.id
                          );
                          if (isAlreadySelected) return null;

                          return (
                            <button
                              key={field.id}
                              type="button"
                              className="min-h-xxl px-3 text-left text-sm hover:bg-[#F6F6F6]"
                              onClick={() => {
                                addCustomField(field);
                                setIsSearchPopoverOpen(false);
                              }}
                            >
                              {field.name}
                            </button>
                          );
                        })}
                      </>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              {isFilterActive && (
                <Button color="tertiary" size="md" className="ml-auto" onClick={clearSearchFilters}>
                  <MdOutlineClose className="size-icon-xs text-primary" />
                  <p className="text-sm font-bold text-primary">検索条件をクリア</p>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
